import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DRiLogo from '../assets/DRi_logo.png';
import VsenseLogo from '../assets/logo.png';
import DashboardIcon from '../assets/icons/Dashboard.svg';
import DashboardActive from '../assets/icons/Dashboard_Red.svg';
import ReportsIcon from '../assets/icons/Reports.svg';
import ReportsActive from '../assets/icons/Reports_Red.svg';
import AlertsIcon from '../assets/icons/Alerts.svg';
import AlertsActive from '../assets/icons/Alerts_Red.svg';
import AiIcon from '../assets/icons/Ai_Insights.svg';
import AiActive from '../assets/icons/Ai_Insights_Red.svg';
import SettingsIcon from '../assets/icons/Settings.svg';
import SettingsActive from '../assets/icons/Settings_Red.svg';

const menuItems = [
  {
    title: 'Dashboard',
    icon: DashboardIcon,
    activeIcon: DashboardActive,
    items: [
      { name: 'Overview', path: '/dashboard/overview' },
      { name: 'Unit View', path: '/dashboard/unit' },
      { name: 'Device Logs', path: '/dashboard/device-logs' },
    ],
  },
  {
    title: 'Reports',
    icon: ReportsIcon,
    activeIcon: ReportsActive,
    items: [
      { name: 'New Report', path: '/reports/new' },
      { name: 'Current Reports', path: '/reports/current' },
    ],
  },
  {
    title: 'Alerts',
    icon: AlertsIcon,
    activeIcon: AlertsActive,
    items: [
      { name: 'New Alert', path: '/alerts/new' },
      { name: 'Current Alerts', path: '/alerts/current' },
    ],
  },
  {
    title: 'AI Insights',
    icon: AiIcon,
    activeIcon: AiActive,
    items: [
      { name: 'Predictions', path: '/ai/predictions' },
      { name: 'Ask AI', path: '/ai/ask' },
    ],
  },
  {
    title: 'Settings',
    icon: SettingsIcon,
    activeIcon: SettingsActive,
    items: [
      { name: 'Settings', path: '/settings' },
    ],
  },
];

function Sidebar({ mobileOpen, setMobileOpen, collapsed, setCollapsed }) {
  const [openIndex, setOpenIndex] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (mobileOpen) {
      setCollapsed(false);
    }
  }, [mobileOpen, setCollapsed]);

  useEffect(() => {
    const activeIndex = menuItems.findIndex((menu) =>
      menu.items.some((item) => location.pathname.startsWith(item.path))
    );

    if (!collapsed && activeIndex !== -1) {
      setOpenIndex(activeIndex);
    }
  }, [location.pathname, collapsed]);

  const toggle = (index) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenIndex(index);
    } else {
      setOpenIndex(openIndex === index ? null : index);
    }
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
    setOpenIndex(null);
  };

  return (
    <>
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}
      <aside
        className={`group fixed inset-y-0 left-0 z-30 flex h-full transform flex-col overflow-hidden border-r border-white/5 bg-[#090c12]/95 text-gray-200 shadow-2xl shadow-black/40 transition-all duration-500 backdrop-blur-xl ${
          mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'
        } md:static md:translate-x-0 ${
          collapsed ? 'md:w-20' : 'md:w-64'
        }`}
      >
        <div className="relative px-5 pt-6 pb-4">
          <img
            src={VsenseLogo}
            alt="V-Sense logo"
            className={`mx-auto w-auto object-contain transition-all duration-300 ${collapsed ? 'h-10' : 'h-14'}`}
          />
          <div className="pointer-events-none absolute inset-x-5 bottom-0 h-px bg-gradient-to-r from-transparent via-[#bf1d2e]/80 to-transparent" />
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute right-4 top-5 flex h-9 w-9 items-center justify-center rounded-xl border border-white/5 bg-white/5 text-white transition-all duration-300 hover:bg-white/10 md:hidden"
          aria-label="Close navigation"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <nav className="flex-1 space-y-2 overflow-y-auto px-3 pb-10">
          {menuItems.map((menu, index) => {
            const isMenuActive = menu.items.some((item) =>
              location.pathname.startsWith(item.path)
            );

            return (
              <motion.div
                key={menu.title}
                layout
                className="rounded-2xl border border-transparent bg-white/0 transition-colors duration-300 hover:border-white/5"
              >
                <motion.button
                  layout
                  onClick={() => toggle(index)}
                  className={`group flex w-full items-center justify-between rounded-2xl px-3 py-3 text-left text-sm font-medium transition-all duration-300 ${
                    isMenuActive ? 'text-white' : 'text-white/60 hover:text-white'
                  } ${collapsed ? 'justify-center px-2' : ''}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-white/5 p-2 shadow-inner shadow-black/30 transition-all duration-300 ${
                      isMenuActive ? 'border-[#bf1d2e]/60 bg-[#bf1d2e]/15 shadow-[#bf1d2e]/30' : ''
                    } ${collapsed ? 'h-9 w-9' : ''}`}>
                      <img
                        src={
                          isMenuActive || openIndex === index
                            ? menu.activeIcon
                            : menu.icon
                        }
                        alt=""
                        className="h-full w-full object-contain"
                      />
                    </span>
                    {!collapsed && (
                      <span className="flex flex-col">
                        <span>{menu.title}</span>
                        <span className="text-xs text-white/40">
                          {isMenuActive ? 'Active' : 'Navigate'}
                        </span>
                      </span>
                    )}
                  </span>
                  {!collapsed && (
                    <motion.span
                      animate={{ rotate: openIndex === index ? 180 : 0 }}
                      className="text-white/40"
                    >
                      ▾
                    </motion.span>
                  )}
                  {collapsed && (
                    <span className="sr-only">{menu.title}</span>
                  )}
                </motion.button>
                <AnimatePresence initial={false}>
                  {openIndex === index && !collapsed && (
                    <motion.ul
                      key={`${menu.title}-items`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="mt-1 space-y-1 overflow-hidden pb-3 pl-4"
                    >
                      {menu.items.map((item) => (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                              `flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-all duration-200 ${
                                isActive
                                  ? 'bg-[#bf1d2e]/20 text-white shadow-inner shadow-[#bf1d2e]/30'
                                  : 'text-white/60 hover:bg-white/5 hover:text-white'
                              }`
                            }
                          >
                            <span>{item.name}</span>
                          </NavLink>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </nav>

        <footer className="relative mt-auto px-4 pb-5 pt-4">
          <div className="rounded-2xl border border-white/5 bg-white/5 px-4 py-3 text-xs text-white/60 shadow-inner shadow-black/30">
            {!collapsed ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">
                  Powered by
                </span>
                <img src={DRiLogo} alt="DRI logo" className="h-7" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <img src={DRiLogo} alt="DRI logo" className="h-7" />
                <span className="text-[9px] uppercase tracking-[0.45em] text-white/40">
                  Powered
                </span>
              </div>
            )}
          </div>
          <button
            onClick={toggleCollapse}
            className="absolute -top-4 right-6 hidden h-10 w-10 items-center justify-center rounded-2xl border border-white/5 bg-white/10 text-white shadow-lg shadow-black/30 transition-transform duration-300 hover:-translate-y-0.5 hover:bg-white/20 md:flex"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ rotate: collapsed ? 180 : 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="h-4 w-4"
            >
              <path d="M15 18l-6-6 6-6" />
            </motion.svg>
          </button>
        </footer>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#bf1d2e]/10 via-transparent to-transparent" />
      </aside>
    </>
  );
}

export default Sidebar;
